import { useEffect, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonTypes } from "@iddataweb/idw-common-ui/enums";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_COMPONENTS_URL } from "@constants/routes";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { ApAssertion, Assertion, AvailableAssertionStatus, DeployedPolicyComponent, ServiceOffering } from "@idw-types/data/reference-data/generic";
import { FormattedPolicyComponent, PolicyComponent } from "@idw-types/data/reference-data/generic/policy-component";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { navigateToUtil } from "@utils/navigation-util";
import { PolicyComponentConfigurationTab } from "./policy-component-configuration-tab/policy-component-configuration-tab";
import { PolicyComponentGeneralTab } from "./policy-component-general-tab/policy-component-general-tab";
import { PolicyComponentAssociatedPoliciesTab } from "./policy-component-associated-policies-tab/policy-component-associated-policies-tab-container";
import { PolicyComponentAssociatedPolicy } from "@idw-types/policy-component-associated-policy";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { getCreatePolicyComponentValidationErrors } from "@utils/validation/create-policy-component/create-policy-component-validation-util";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { ModalTypes } from "@enums/modal-types";
import { useModalContext } from "@context/use-modal-context";
import styles from "./policy-component-details-component.less";
import { TableState } from "@tanstack/react-table";

type PolicyComponentDetailsComponentProps = {
    isDisabled: boolean;
    policyComponentDetails: FormattedPolicyComponent;
    axnCompanies: Array<AxnCompany>;
    policyComponents: Array<DeployedPolicyComponent>;
    assertions: Array<Assertion>;
    apAssertions: Array<ApAssertion>;
    serviceOfferings: Array<ServiceOffering>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    acquiredAttributes: Array<AxnAcquiredAttribute>;
    associatedPolicies: Array<PolicyComponentAssociatedPolicy>;
    associatedPoliciesTableState: TableState;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    handleSave: (policyComponentDetails: FormattedPolicyComponent) => Promise<PolicyComponent>;
}

export const PolicyComponentDetailsComponent = (props: PolicyComponentDetailsComponentProps) => {
    const {
        isDisabled,
        policyComponentDetails,
        axnCompanies,
        policyComponents,
        assertions,
        apAssertions,
        serviceOfferings,
        axnAttributeTypes,
        axnAttributeTypeFields,
        acquiredAttributes,
        associatedPolicies,
        associatedPoliciesTableState,
        axnAvailableAssertionStatuses,
        handleSave
    } = props;

    const navigate = useNavigate();
    const routerLocation = useLocation();
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [policyComponentDetailsState, setPolicyComponentDetailsState] = useState<FormattedPolicyComponent>(policyComponentDetails);
    const modalContext = useModalContext();

    const stateIsUnchanged = useMemo(() => {
        return isEqual(policyComponentDetails, policyComponentDetailsState);
    }, [policyComponentDetails, policyComponentDetailsState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-policy-component-changes",
        modalType: ModalTypes.DiscardPolicyComponentChanges,
        stateIsUnchanged
    });

    const onClickClose = () => {
        navigateToUtil(POLICY_COMPONENTS_URL, navigate, routerLocation.pathname);
    }

    useEffect(() => {
        setPolicyComponentDetailsState(policyComponentDetails);
    }, [policyComponentDetails]);

    const confirmMessage = useMemo(() => {
        const associatedPoliciesMessage = associatedPolicies.map((associatedPolicy, index) => {
            const lines: Array<{ key: string; value: string; }> = [];
            lines.push({ key: "Policy ID", value: `${associatedPolicy.policyID}` });

            if (associatedPolicy.serviceName && associatedPolicy.serviceApiKey) {
                lines.push({ key: `Service`, value: `${associatedPolicy.serviceName} (${associatedPolicy.serviceApiKey})` });
            }
            if (associatedPolicy.relyingParty) {
                lines.push({ key: `Relying Party`, value: `${associatedPolicy.relyingParty.name}` });
            }
            if (associatedPolicy.organization) {
                lines.push({ key: `Organization`, value: `${associatedPolicy.organization.name}` });
            }
            return (
                <div className={styles.affectedPolicy} key={`policy-${associatedPolicy.policyID}-${index}`}>
                    {lines.map((line, lineIndex) => (
                        <p key={`policy-id-${associatedPolicy.policyID}-line-${lineIndex}`}>
                            <b>{line.key}: </b>{line.value}
                        </p>
                    ))}
                </div>
            );
        });

        return (
            <div>
                <p>{resources.modals.updatePolicyComponent.confirmMessageTitle}</p>
                <div className={styles.associatedPoliciesMessageContainer}>
                    {associatedPoliciesMessage}
                </div>
                <p>{resources.modals.updatePolicyComponent.confirmMessageDescription}</p>
            </div>
        );
    }, [associatedPolicies]);

    const onSubmit = async () => {
        setRequestInProgress(true);
        await handleSave(policyComponentDetailsState);
        setRequestInProgress(false);
    }

    const onClickSave = async () => {
        if (associatedPolicies.length > 0) {
            renderConfirmSavePolicyComponentModal(confirmMessage, onSubmit);
        } else {
            onSubmit();
        }
    };

    const renderConfirmSavePolicyComponentModal = (
        confirmMessage: string | React.ReactNode,
        onSubmit: (policyComponentDetailsState: FormattedPolicyComponent) => Promise<void>
    ) => {

        const renderModal = (onModalClose: () => void) => {
            return (
                <ConfirmModal
                    id={"confirm-save-policy-component"}
                    title={resources.modals.updatePolicyComponent.title}
                    confirmMessage={confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resources.common.buttons.save.label}
                    onSubmit={null}
                    onSubmitAsync={onSubmit}
                    onModalClose={onModalClose}
                    customProps={policyComponentDetailsState}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    };

    const isPolicyComponentNameAlreadyUsed = useMemo(() => {
        return policyComponents.some(policyComponent => policyComponentDetails.name !== policyComponentDetailsState.name && policyComponent.name === policyComponentDetailsState.name);
    }, [policyComponents, policyComponentDetailsState.name]);

    const validationErrors = useMemo(() => {
        return getCreatePolicyComponentValidationErrors(policyComponentDetailsState, isPolicyComponentNameAlreadyUsed);
    }, [policyComponentDetailsState, isPolicyComponentNameAlreadyUsed]);

    return <div className={styles.policyComponentDetailsComponent}>
        <div className={styles.headerContainer}>
            <div className={styles.headerRow}>
                <span className={styles.policyComponentName}>{policyComponentDetails.name}</span>

                <div className={styles.buttonContainer}>
                    <Button id="policy-component-details-close-button" variant={"outline-secondary"} onClick={onClickClose}>{resources.common.buttons.close.label}</Button>

                    {policyComponentDetailsState.permissions.canModify && (
                        <ButtonWithSpinner
                            id="policy-component-details-save-button"
                            disabled={stateIsUnchanged || requestInProgress || validationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                            label={resources.common.buttons.save.label}
                            type={ButtonTypes.SUBMIT}
                            onClick={onClickSave}
                            variant={"primary"}
                        />
                    )}
                </div>
            </div>
        </div>

        <TabContainer>
            {/* @ts-ignore */}
            <Tabs id="policyComponent" fill={false} unmountOnExit={true}>
                <Tab eventKey="general" title={validationErrors.generalStep.tabTitle}>
                    <PolicyComponentGeneralTab
                        isDisabled={isDisabled}
                        policyComponentState={policyComponentDetailsState}
                        axnCompanies={axnCompanies}
                        validationErrors={validationErrors.generalStep}
                        setPolicyComponentDetailsState={setPolicyComponentDetailsState}
                    />
                </Tab>

                <Tab eventKey="configuration" title={validationErrors.triggerConfiguration.tabTitle}>
                    <PolicyComponentConfigurationTab
                        isDisabled={isDisabled}
                        policyComponentState={policyComponentDetailsState}
                        policyComponents={policyComponents}
                        assertions={assertions}
                        apAssertions={apAssertions}
                        serviceOfferings={serviceOfferings}
                        axnAttributeTypes={axnAttributeTypes}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        acquiredAttributes={acquiredAttributes}
                        axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                        setPolicyComponentDetailsState={setPolicyComponentDetailsState}
                    />
                </Tab>

                <Tab eventKey="associated-policies" title={resources.pages.policyComponentDetails.tabs.associatedPolicies.title}>
                    <PolicyComponentAssociatedPoliciesTab
                        associatedPolicies={associatedPolicies}
                        associatedPoliciesTableState={associatedPoliciesTableState}
                    />
                </Tab>

            </Tabs>
        </TabContainer>

    </div>
}